import React, { useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { translateProblemTypeDescription, translateProblemTypeName } from '../../utils/problemTypeDisplay';

export interface StationProblemType {
  id: number;
  name: string;
  description: string | null;
  is_active?: boolean;
}

interface StationNotesModalProps {
  /** large_warehouse_items.id — the note belongs to the rewinding entry, not to the line. */
  warehouseItemId: number;
  itemLabel: string;
  currentNote?: string | null;
  noteBy?: string | null;
  noteAt?: string | null;
  problemTypes: StationProblemType[];
  dateLocale?: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Note editor for the rewinding station. Deliberately separate from the line's notes modal:
 * the station keeps its own note on the warehouse entry, so reporting a problem here never
 * overwrites what the production line reported on the same item.
 */
const StationNotesModal: React.FC<StationNotesModalProps> = ({
  warehouseItemId,
  itemLabel,
  currentNote,
  noteBy,
  noteAt,
  problemTypes,
  dateLocale = 'pl-PL',
  onClose,
  onSaved,
}) => {
  const { t } = useLanguage();
  // Uwaga zapisana jest tekstem ("nazwa - opis"), więc dopasowujemy typ po prefiksie nazwy.
  const matched = problemTypes.find((pt) => (currentNote || '').startsWith(pt.name));
  const [selectedProblemTypeId, setSelectedProblemTypeId] = useState<number | null>(matched?.id ?? null);
  const [freeText, setFreeText] = useState(matched ? '' : (currentNote || ''));
  const [saving, setSaving] = useState(false);

  const activeTypes = problemTypes.filter((pt) => pt.is_active !== false);

  const save = async (clear = false) => {
    setSaving(true);
    try {
      await api.put(`/warehouse/large/${warehouseItemId}/notes`, {
        problem_type_id: clear ? null : selectedProblemTypeId,
        note: clear ? null : (selectedProblemTypeId ? null : freeText.trim() || null),
      });
      onSaved();
      onClose();
    } catch (err: any) {
      alert(err?.response?.data?.message || t('common.errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const canSave = !saving && (selectedProblemTypeId !== null || freeText.trim() !== '');

  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000,
      }}
    >
      <div
        style={{
          backgroundColor: 'white', padding: 30, borderRadius: 12,
          maxWidth: 560, width: '90%', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        <h3 style={{ marginTop: 0, color: '#667eea' }}>
          📋 {t('largeWarehouse.notesTitle')}
        </h3>
        <div style={{
          marginTop: 12, padding: 12, backgroundColor: '#e7f3ff',
          border: '1px solid #667eea', borderRadius: 8, color: '#4a5568', fontSize: 12,
        }}>
          <strong>{itemLabel}</strong>
          <div style={{ marginTop: 4, color: '#64748b' }}>{t('largeWarehouse.notesScopeHint')}</div>
        </div>

        {currentNote && (
          <div style={{
            marginTop: 12, padding: 12, backgroundColor: '#fff3cd',
            border: '1px solid #ffc107', borderRadius: 8, fontSize: 13, color: '#856404',
          }}>
            ⚠️ {currentNote}
            {(noteBy || noteAt) && (
              <div style={{ marginTop: 4, fontSize: 11, color: '#a07d12' }}>
                {noteBy || '—'}
                {noteAt ? ` · ${new Date(noteAt).toLocaleString(dateLocale)}` : ''}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
            {t('largeWarehouse.notesSelectProblem')}
          </label>
          <select
            value={selectedProblemTypeId ?? ''}
            onChange={(e) => {
              const value = e.target.value ? parseInt(e.target.value, 10) : null;
              setSelectedProblemTypeId(value);
              if (value !== null) setFreeText('');
            }}
            style={{
              width: '100%', padding: 12, boxSizing: 'border-box',
              border: '2px solid #e1e8ed', borderRadius: 8, fontSize: 14, backgroundColor: 'white',
            }}
          >
            <option value="">{t('largeWarehouse.notesNoProblemType')}</option>
            {activeTypes.map((pt) => (
              <option key={pt.id} value={pt.id}>
                {translateProblemTypeName(pt.name, t)}
                {pt.description ? ` — ${translateProblemTypeDescription(pt.description, t)}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
            {t('largeWarehouse.notesFreeText')}
          </label>
          <textarea
            value={freeText}
            onChange={(e) => { setFreeText(e.target.value); if (e.target.value) setSelectedProblemTypeId(null); }}
            rows={3}
            maxLength={1000}
            placeholder={t('largeWarehouse.notesFreeTextPlaceholder')}
            style={{
              width: '100%', padding: 12, boxSizing: 'border-box',
              border: '2px solid #e1e8ed', borderRadius: 8, fontSize: 14, resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => save(true)}
            disabled={saving || !currentNote}
            style={{
              padding: '10px 20px', backgroundColor: 'white', color: currentNote ? '#dc3545' : '#9ca3af',
              border: `1px solid ${currentNote ? '#dc3545' : '#d3d3d3'}`, borderRadius: 8,
              cursor: currentNote && !saving ? 'pointer' : 'not-allowed', fontWeight: 500,
            }}
          >
            🗑 {t('largeWarehouse.notesDelete')}
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '10px 20px', backgroundColor: '#6c757d', color: 'white',
                border: 'none', borderRadius: 8, cursor: 'pointer',
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => save()}
              disabled={!canSave}
              style={{
                padding: '10px 20px', backgroundColor: canSave ? '#667eea' : '#d3d3d3', color: 'white',
                border: 'none', borderRadius: 8, cursor: canSave ? 'pointer' : 'not-allowed',
                fontWeight: 500, opacity: canSave ? 1 : 0.6,
              }}
            >
              {saving ? '...' : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StationNotesModal;
